import React, { useState } from 'react';
import { Box, Container, Grid, Typography, InputBase, IconButton, Link, Dialog, Switch, CircularProgress, Slide } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { subscribeNewsletter, resetNewsletter } from '../../store/slices/newsletterSlice';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CloseIcon from '@mui/icons-material/Close';
import logo from '../../assets/images/logo.png';
import '../../App.css';
import { useNavigate, useLocation } from 'react-router-dom';
import sendIcon from '../../assets/images/send-fill-icon.png';
import DisclaimerPanel from './DisclaimerPanel';
import CookiePolicyPanel from './CookiePolicyPanel';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { status, message } = useSelector((state) => state.newsletter);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const [openDisclaimer, setOpenDisclaimer] = useState(false);
  const [openCookies, setOpenCookies] = useState(false);

  const handleOpenDisclaimer = () => setOpenDisclaimer(true);
  const handleCloseDisclaimer = () => setOpenDisclaimer(false);

  const handleCloseCookies = () => setOpenCookies(false);

  const [openCookiePolicy, setOpenCookiePolicy] = useState(false);
  const handleOpenCookiePolicy = () => setOpenCookiePolicy(true);
  const handleCloseCookiePolicy = () => setOpenCookiePolicy(false);

  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [preferencesEnabled, setPreferencesEnabled] = useState(false);
  const [essentialExpanded, setEssentialExpanded] = useState(true);
  const [analyticsExpanded, setAnalyticsExpanded] = useState(true);
  const [preferencesExpanded, setPreferencesExpanded] = useState(false);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();

    if (status === 'loading') return;

    if (!newsletterEmail.trim()) {
      setSuccessMessage('');
      setEmailError('Email is required');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(newsletterEmail)) {
      setSuccessMessage('');
      setEmailError('Please enter a valid email');
      return;
    }

    setEmailError('');
    setSuccessMessage('');

    dispatch(subscribeNewsletter(newsletterEmail)).then((result) => {
      if (result.meta.requestStatus === 'fulfilled') {
        setNewsletterEmail('');
        setSuccessMessage('Subscribed Successfully');
        setTimeout(() => {
          setSuccessMessage('');
          dispatch(resetNewsletter());
        }, 3000);
      }
    });
  };

  const companyLinks = [
    { name: 'Home', id: 'footer-link-home', path: '/' },
    { name: 'Services', id: 'footer-link-services', path: '/services' },
    { name: 'About Us', id: 'footer-link-about-us', path: '/about' },
    { name: 'Legal & Policies', id: 'footer-link-legal', path: '/legal_and_polices' },
    { name: 'Contact Us', id: 'footer-link-get-started', path: '/contact' }
  ];

  const legalLinks = [
    { name: 'Privacy Policy', id: 'footer-link-privacy-policy', path: '/legal_and_polices', tab: 0, section: 'privacy' },
    { name: 'Cookie Policy', id: 'footer-link-cookie-policy', path: '/legal_and_polices', tab: 0, section: 'cookie', popup: 'cookiePolicy' },
    { name: 'Risk Disclosure', id: 'footer-link-risk-disclosure', path: '/legal_and_polices', tab: 1, section: 'risk' },
    { name: 'AML / CTF Policy', id: 'footer-link-aml-ctf-policy', path: '/legal_and_polices', tab: 2, section: 'aml' },
    { name: 'Complaints Handling', id: 'footer-link-complaints', path: '/legal_and_polices', tab: 3, section: 'complaints' },
  ];

  const socialLinks = [
    { icon: <InstagramIcon sx={{ fontSize: '1.4rem' }} />, url: 'https://instagram.com/payiblehq', id: 'footer-social-instagram' },
    { icon: <TwitterIcon sx={{ fontSize: '1.4rem' }} />, url: 'https://x.com/payiblehq', id: 'footer-social-twitter' },
    { icon: <LinkedInIcon sx={{ fontSize: '1.4rem' }} />, url: 'https://www.linkedin.com/company/payible', id: 'footer-social-linkedin' }
  ];


  const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="left" ref={ref} {...props} />
  })

  return (
    <Box
      component="footer"
      id="footer-section"
      sx={{
        backgroundColor: 'var(--color-bg)',
        color: '#FFFFFF',
        pt: { xs: 8, md: 3 },
        pb: { xs: 6, md: 8 },
        fontFamily: '"Plus Jakarta Sans", sans-serif',
      }}
    >
      <Container maxWidth="lg">
        <Box
          component="img"
          src={logo}
          alt="Payible"
          id="footer-logo"
          onClick={() => navigate('/')}
          sx={{
            width: { xs: 120, md: 135 },
            height: { xs: 32, md: 40 },
            mb: 2.5,
            display: 'block',
            mx: { xs: 'auto', sm: 0 },
            cursor: 'pointer',
          }}
        />
        {/* Main Footer Links Section */}
        <Grid
          container
          sx={{
            alignItems: 'flex-start',
            mb: 6,
            justifyContent: 'flex-start',
            rowGap: { xs: 3, sm: 4 },
            '@media (min-width: 768px)': {
              justifyContent: 'space-between',
              rowGap: 0,
            }
          }}
        >

          {/* Brand Info Column */}
          <Grid
            sx={{
              width: '100%',
              flexBasis: '100%',
              maxWidth: '100%',
              '@media (min-width: 768px)': {
                width: '25%',
                flexBasis: '25%',
                maxWidth: '25%',
              }
            }}
          >

            <Box sx={{ mb: { xs: 0, sm: 3 } }}>
              <Typography
                className="footer-brand-desc"
                sx={{
                  fontSize: '0.9rem',
                  mb: 3.5,
                  maxWidth: '340px',
                  textAlign: { xs: 'center', sm: 'left' },
                  mx: { xs: 'auto', sm: 0 },
                }}
              >
                Payible enables instant, non-custodial fiat to crypto conversion for individuals and businesses.
              </Typography>
              {/* Social Icons */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2.5,
                  justifyContent: {
                    xs: 'center',
                    sm: 'flex-start'
                  },
                }}
              >
                {socialLinks.map((social) => (
                  <IconButton
                    key={social.id}
                    id={social.id}
                    component="a"
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: "#fff",
                      padding: 0,
                      minWidth: 'auto',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: 'var(--color-primary)',
                        backgroundColor: 'transparent',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {social.icon}
                  </IconButton>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Company Column */}
          <Grid
            sx={{
              width: '50%',
              flexBasis: '50%',
              maxWidth: '50%',
              '@media (min-width: 768px)': {
                width: '15%',
                flexBasis: '15%',
                maxWidth: '15%',
              }
            }}
          >
            <Typography
              variant="subtitle1"
              className="footer-section-title"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                mb: 3,
                textAlign: { xs: 'center', sm: 'left' },
              }}
            >
              Company
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                alignItems: { xs: 'center', sm: 'flex-start' },
              }}
            >
              {companyLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.id}
                    id={link.id}
                    href={link.path}
                    underline="none"
                    className={`footer-link${isActive ? 'footer-link-active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(link.path);
                    }}
                    sx={{
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      textAlign: { xs: 'center', sm: 'left' },
                      color: isActive ? 'var(--color-primary)' : 'var(--color-primary)',
                      fontWeight: isActive ? 700 : 400,
                      transition: 'color 0.2s ease',
                      '&:hover': { color: 'var(--color-primary)' },
                    }}
                  >
                    {link.name}
                  </Link>
                );
              })}
            </Box>
          </Grid>

          {/* Legal & Policies Column */}
          <Grid
            sx={{
              width: '50%',
              flexBasis: '50%',
              maxWidth: '50%',
              '@media (min-width: 768px)': {
                width: '20%',
                flexBasis: '20%',
                maxWidth: '20%',
              }
            }}
          >
            <Typography
              variant="subtitle1"
              className="footer-section-title"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                mb: 3,
                textAlign: { xs: 'center', sm: 'left' },
              }}
            >
              Legal & Policies
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                alignItems: { xs: 'center', sm: 'flex-start' },
              }}
            >
              {legalLinks.map((link) => {
                const params = new URLSearchParams(location.search);
                const currentSection = params.get('section');
                const currentTab = parseInt(params.get('tab') ?? '0', 10);
                const isActive = location.pathname === link.path &&
                  currentTab === link.tab &&
                  (currentSection ? currentSection === link.section : link.section === 'privacy');
                return (
                  <Link
                    key={link.name}
                    id={link.id}
                    href={`${link.path}?tab=${link.tab}&section=${link.section}`}
                    underline="none"
                    className={`footer-link${isActive ? 'footer-link-active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      if (link.popup === 'cookiePolicy') {
                        handleOpenCookiePolicy();
                      } else {
                        navigate(`${link.path}?tab=${link.tab}&section=${link.section}`);
                      }
                    }}
                    sx={{
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      textAlign: { xs: 'center', sm: 'left' },
                      color: isActive ? 'var(--color-primary)' : 'rgba(255,255,255,0.7)',
                      fontWeight: isActive ? 700 : 400,
                      transition: 'color 0.2s ease',
                      '&:hover': { color: 'var(--color-primary)' },
                    }}
                  >
                    {link.name}
                  </Link>
                );
              })}
            </Box>
          </Grid>

          {/* Newsletter Column */}
          <Grid
            sx={{
              width: '100%',
              flexBasis: '100%',
              maxWidth: '100%',
              '@media (min-width: 768px)': {
                width: '30%',
                flexBasis: '30%',
                maxWidth: '30%',
              }
            }}
          >
            <Typography
              variant="subtitle1"
              className="footer-section-title"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                mb: 3,
                textAlign: { xs: 'center', sm: 'left' },
              }}
            >
              Keep up with the latest
            </Typography>
            <Typography
              className="footer-newsletter-desc"
              sx={{
                fontSize: {
                  xs: "0.8rem",
                  sm: "0.9rem",
                  md: "1rem",
                  lg: "1.05rem",
                },
                lineHeight: {
                  xs: 1.6,
                  md: 1.8,
                },
                mb: { xs: 2, md: 3 },
                maxWidth: {
                  xs: "100%",
                  sm: "500px",
                  md: "600px",
                },
                textAlign: { xs: 'center', sm: 'left' },
                mx: { xs: 'auto', sm: 0 },
              }}
            >
              Join our newsletter for updates on digital asset markets,
              product updates, and regulatory notices.
            </Typography>

            <Box
              component="form"
              onSubmit={handleNewsletterSubmit}
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                padding: { xs: '2px 4px 2px 8px', sm: '4px 6px 4px 16px' },
                border: status === 'error'
                  ? '2px solid #EF4444'
                  : status === 'success'
                    ? '2px solid var(--color-primary)'
                    : '2px solid transparent',
                transition: 'all 0.3s ease',
                '&:focus-within': {
                  borderColor: 'var(--color-primary)',
                  boxShadow: '0 0 12px rgba(122, 212, 197, 0.3)',
                },
                maxWidth: { xs: '400px', sm: 'none' },
                mx: { xs: 'auto', sm: 0 },
                opacity: status === 'loading' ? 0.8 : 1,
              }}
            >
              <InputBase
                placeholder="Enter your email"
                type="text"
                id="footer-newsletter-email"
                value={newsletterEmail}
                onChange={(e) => { setNewsletterEmail(e.target.value); if (emailError) setEmailError(''); }}
                disabled={status === 'loading' || status === 'success'}
                slotProps={{ input: { className: "footer-newsletter-input" } }}
                sx={{
                  flex: 1,
                  color: '#000000',
                  fontSize: { xs: '0.75rem', sm: '0.9rem' },
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                }}
              />
              <IconButton
                type="submit"
                id="footer-newsletter-submit-btn"
                disabled={status === 'loading' || status === 'success'}
                sx={{
                  color: 'var(--color-primary)',
                  padding: { xs: '4px', sm: '8px' },
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' },
                  '&.Mui-disabled': { opacity: 0.5 },
                }}
              >
                {status === 'loading'
                  ? <CircularProgress size={20} sx={{ color: 'var(--color-primary)' }} />
                  : <img src={sendIcon} alt='send icon' />
                }
              </IconButton>
            </Box>

            {successMessage && (
              <Typography
                sx={{
                  mt: 1,
                  fontSize: '0.78rem',
                  color: 'var(--color-primary)',
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                {successMessage}
              </Typography>
            )}

            {/* Validation error */}
            {emailError && (
              <Typography
                sx={{
                  mt: 1,
                  fontSize: '0.78rem',
                  color: '#EF4444',
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                {emailError}
              </Typography>
            )}

            {/* Status message */}
            {showSuccess && (
              <Typography
                sx={{
                  mt: 1.5,
                  fontSize: '0.78rem',
                  color: 'var(--color-primary)',
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                Email submitted successfully!
              </Typography>
            )}
            {!showSuccess && status === 'error' && message && (
              <Typography
                sx={{
                  mt: 1.5,
                  fontSize: '0.78rem',
                  color: '#EF4444',
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                {message}
              </Typography>
            )}
          </Grid>
        </Grid>

        {/* Divider Line */}
        <Box
          sx={{
            height: '1px',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            mb: 4,
          }}
        />

        {/* Footer Bottom (Copyright & Licensing Disclosures) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 1.5,
            width: "100%",
          }}
        >
          <Typography
            variant="body2"
            className="footer-bottom-copyright"
            sx={{
              fontSize: "0.8rem",
              fontFamily: "var(--font-primary)",
              textAlign: "left",
              width: "100%",
              m: 0,
            }}
          >
            © 2026 Payible. All Rights Reserved.
          </Typography>

          <Typography
            variant="body2"
            className="footer-bottom-disclosure"
            sx={{
              fontSize: "0.8rem",
              fontFamily: "var(--font-secondary)",
              lineHeight: 1.6,
              textAlign: "left",
              width: "100%",
              m: 0,
            }}
          >
            Payible Pty Ltd (ACN 643368496) is a Digital Currency Provider that is
            licensed and regulated by AUSTRAC under DCE registration number
            DCE100706348.
            <Link
              href="#"
              id="footer-disclaimer-link"
              onClick={(e) => {
                e.preventDefault();
                handleOpenDisclaimer();
              }}
              sx={{
                color: "var(--color-primary)",
                textDecoration: "underline",
                fontWeight: 600,
                ml: 0.5,
                transition: "opacity 0.2s ease",
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            >
              [ Read Full Disclaimer ]
            </Link>
          </Typography>


        </Box>
      </Container>

      {openDisclaimer && <DisclaimerPanel onClose={handleCloseDisclaimer} />}

      {/* Cookie Settings Dialog */}
      <Dialog
        open={openCookies}
        onClose={handleCloseCookies}
        TransitionComponent={Transition}
        maxWidth="xs"
        fullWidth
        scroll="paper"
        sx={{
          '& .MuiDialog-container': {
            justifyContent: 'flex-end',
            alignItems: 'flex-start',
          },
        }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: '#1c1c28',
              color: '#FFFFFF',
              borderRadius: '12px 0 0 12px',
              border: '1px solid rgba(255,255,255,0.08)',
              p: 0,
              overflow: 'hidden',
              m: 0,
              height: '100vh',
              maxHeight: '100vh',
            }
          }
        }}
      >
        <Box sx={{ backgroundColor: '#1c1c28', display: 'flex', flexDirection: 'column', height: '100%' }}>

          {/* Header */}
          <Box sx={{ p: { xs: 2.5, sm: 3 }, pb: 0 }}>
            <IconButton
              onClick={handleCloseCookies}
              sx={{ color: '#FFFFFF', p: 0, mb: 1.5, '&:hover': { backgroundColor: 'transparent', opacity: 0.7 } }}
            >
              <CloseIcon sx={{ fontSize: '1.5rem' }} />
            </IconButton>
            <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: { xs: '1.2rem', sm: '1.35rem' }, color: '#FFFFFF', mb: 2 }}>
              Cookie settings
            </Typography>
            <Typography sx={{ fontFamily: '"Open Sans", sans-serif', fontSize: '0.82rem', lineHeight: 1.75, color: 'rgba(255,255,255,0.7)', mb: 1.5 }}>
              This panel allows you to customize your privacy preferences on Payible. We use cookies to maintain infrastructure security, verify your account identity, and keep your transaction pipeline safe.
            </Typography>
            <Typography sx={{ fontFamily: '"Open Sans", sans-serif', fontSize: '0.82rem', lineHeight: 1.75, color: 'rgba(255,255,255,0.7)', mb: 2 }}>
              You can freely toggle your performance and analytical tracking preferences below. Adjusting these settings helps our team optimize live asset chart speeds and platform response times. For full details, read our{' '}
              <Link
                href="#"
                onClick={(e) => { e.preventDefault(); handleCloseCookies(); setTimeout(() => handleOpenCookiePolicy(), 250); }}
                sx={{ color: 'var(--color-primary)', fontWeight: 700, textDecoration: 'underline', fontSize: 'inherit', cursor: 'pointer' }}
              >
                Full Cookie Policy.
              </Link>
            </Typography>
          </Box>

          {/* Scrollable cookie sections */}
          <Box sx={{ overflowY: 'auto', flex: 1, px: { xs: 2.5, sm: 3 }, pb: 2 }}>

            {/* Divider */}
            <Box sx={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.1)', mb: 2 }} />

            {/* Essential Cookies */}
            <Box sx={{ mb: 1.5 }}>
              <Box
                onClick={() => setEssentialExpanded(!essentialExpanded)}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: essentialExpanded ? 1.5 : 0 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '1.3rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1, userSelect: 'none' }}>
                    {essentialExpanded ? '−' : '+'}
                  </Typography>
                  <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600, fontSize: '0.95rem', color: '#FFFFFF' }}>
                    Essential cookies
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '0.82rem', color: 'var(--color-primary)' }}>
                  Always Active
                </Typography>
              </Box>
              {essentialExpanded && (
                <Typography sx={{ fontFamily: '"Open Sans", sans-serif', fontSize: '0.8rem', lineHeight: 1.75, color: 'rgba(255,255,255,0.65)', pl: 3.5 }}>
                  Mandatory for platform security and core functionality. They handle secure session handshakes, verify your authentication states, prevent fraudulent exploits, and maintain active fiat-to-crypto transaction pipelines. Without these, secure platform access is impossible.
                </Typography>
              )}
            </Box>

            <Box sx={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.08)', my: 1.5 }} />

            {/* Analytics Cookies */}
            <Box sx={{ mb: 1.5 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: analyticsExpanded ? 1.5 : 0 }}
              >
                <Box
                  onClick={() => setAnalyticsExpanded(!analyticsExpanded)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', flex: 1 }}
                >
                  <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '1.3rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1, userSelect: 'none' }}>
                    {analyticsExpanded ? '−' : '+'}
                  </Typography>
                  <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600, fontSize: '0.95rem', color: '#FFFFFF' }}>
                    Analytics cookies
                  </Typography>
                </Box>
                <Switch
                  checked={analyticsEnabled}
                  onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--color-primary)' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--color-primary)' },
                  }}
                />
              </Box>
              {analyticsExpanded && (
                <Typography sx={{ fontFamily: '"Open Sans", sans-serif', fontSize: '0.8rem', lineHeight: 1.75, color: 'rgba(255,255,255,0.65)', pl: 3.5 }}>
                  Allows our team to collect aggregated, anonymous data regarding platform traffic. This specifically monitors system latency, server response times, and the loading speed of our live asset grids, directly helping us optimize app performance.
                </Typography>
              )}
            </Box>

            <Box sx={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.08)', my: 1.5 }} />

            {/* Preference Cookies */}
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box
                  onClick={() => setPreferencesExpanded(!preferencesExpanded)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', flex: 1 }}
                >
                  <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '1.3rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1, userSelect: 'none' }}>
                    {preferencesExpanded ? '−' : '+'}
                  </Typography>
                  <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600, fontSize: '0.95rem', color: '#FFFFFF' }}>
                    Preference cookies
                  </Typography>
                </Box>
                <Switch
                  checked={preferencesEnabled}
                  onChange={(e) => setPreferencesEnabled(e.target.checked)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--color-primary)' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--color-primary)' },
                  }}
                />
              </Box>
              {preferencesExpanded && (
                <Typography sx={{ fontFamily: '"Open Sans", sans-serif', fontSize: '0.8rem', lineHeight: 1.75, color: 'rgba(255,255,255,0.65)', pl: 3.5, mt: 1.5 }}>
                  Used to remember your platform preferences such as language, layout, and display settings across sessions.
                </Typography>
              )}
            </Box>
          </Box>

          {/* Bottom buttons */}
          <Box sx={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.1)' }} />
          <Box sx={{ p: { xs: 2.5, sm: 3 }, pt: 2, display: 'flex', gap: 1.5 }}>
            <Box
              onClick={handleCloseCookies}
              sx={{
                flex: 1,
                textAlign: 'center',
                py: 1.3,
                borderRadius: '8px',
                border: '1px solid var(--color-primary)',
                color: 'var(--color-primary)',
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontWeight: 600,
                fontSize: '0.88rem',
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'rgba(100,220,180,0.08)' },
              }}
            >
              Confirm my choices
            </Box>
            <Box
              onClick={handleCloseCookies}
              sx={{
                flex: 1,
                textAlign: 'center',
                py: 1.3,
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.35)',
                color: '#FFFFFF',
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontWeight: 600,
                fontSize: '0.88rem',
                cursor: 'pointer',
                '&:hover': { borderColor: 'rgba(255,255,255,0.7)' },
              }}
            >
              Accept All
            </Box>
          </Box>
        </Box>
      </Dialog>

      {/* Cookie Policy & Framework Dialog */}
      <CookiePolicyPanel open={openCookiePolicy} onClose={handleCloseCookiePolicy} />

    </Box>
  );
};

export default Footer;
